import { DEFAULT_RELATIONSHIP_COLOR } from '@nx/state';
import type { OmitStrict } from '@nx/stdlib';
import type { AnyAction, PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { isNil, keyBy } from 'lodash-es';

import { getOrderedList } from '../../modules/Legend/Popups/helpers';
import migrate from '../../services/migrate';
import { labelColorPalette as defaultPalette } from '../../styles/colors';
import type { Category } from '../../types/category';
import { type Relationship as BloomRelationship } from '../../types/graph';
import type {
  CategoryWithStyle,
  Perspective,
  PerspectiveRelationshipType,
  PerspectiveWithStyle,
  RelationshipTypeWithStyle,
} from '../../types/perspective';
import type { Scene } from '../../types/scene';
import type { CategoryIcon, Style, StyleCategory, StyleRelationshipType, StyleRule } from '../../types/style';
import type { Nullable } from '../../types/utility';
import { REHYDRATE } from '../persistence/constants';
import { getLatestPerspectiveVersion, trasformations } from '../perspectives/migrations';
import { addPerspective, clearPerspectives, loadPerspective, removePerspective } from '../perspectives/perspectives';
import { addScene, clearScenes, duplicateScene } from '../scene/scene';
import { deleteSceneThunk } from '../scene/thunks/delete-scene';
import { fetchSceneThunk } from '../scene/thunks/fetch-scene';
import { storeSceneThunk } from '../scene/thunks/store-scene';
import type { SliceActionsUnion } from '../types';
import {
  DEFAULT_GROUP_COLOR,
  DEFAULT_GROUP_ICON,
  DEFAULT_GROUP_SIZE,
  DEFAULT_GROUP_TEXT_ALIGN,
  DEFAULT_GROUP_TEXT_SIZE,
  DEFAULT_RELATIONSHIP_GROUP_SIZE,
  DEFAULT_RELATIONSHIP_SIZE,
  DEFAULT_UNCATEGORISED_COLOR,
  DEFAULT_UNCATEGORISED_ICON,
  DEFAULT_UNCATEGORISED_SIZE,
  DEFAULT_UNCATEGORISED_TEXT_ALIGN,
  DEFAULT_UNCATEGORISED_TEXT_ALIGN_RELATIONSHIP,
  DEFAULT_UNCATEGORISED_TEXT_SIZE,
  NAME,
  STYLE_TYPE_PERSPECTIVE,
  STYLE_TYPE_SCENE,
} from './styles.const';
import type { Caption, CaptionStyle } from './types';
import { CAPTION_TYPE_REL } from './types';

export type StyleType = typeof STYLE_TYPE_PERSPECTIVE | typeof STYLE_TYPE_SCENE;

export const getOtherCategory = (): OmitStrict<StyleCategory, 'id'> => ({
  color: DEFAULT_UNCATEGORISED_COLOR,
  size: DEFAULT_UNCATEGORISED_SIZE,
  icon: DEFAULT_UNCATEGORISED_ICON,
  captionKeys: [], // make caption backward-compatible for <= Bloom 2.7.1
  captions: [], // caption for >= Bloom 2.8.0
  textSize: DEFAULT_UNCATEGORISED_TEXT_SIZE,
  textAlign: DEFAULT_UNCATEGORISED_TEXT_ALIGN,
  styleRules: [],
});

const groupStyleCategory = {
  color: DEFAULT_GROUP_COLOR,
  captionKeys: [],
  captions: [],
  textSize: DEFAULT_GROUP_TEXT_SIZE,
  textAlign: DEFAULT_GROUP_TEXT_ALIGN,
  styleRules: [],
};

export const getNodeGroupStyleCategory = (): OmitStrict<StyleCategory, 'id'> => ({
  ...groupStyleCategory,
  color: DEFAULT_GROUP_COLOR,
  size: DEFAULT_GROUP_SIZE,
  icon: DEFAULT_GROUP_ICON,
});

export const getRelationshipGroupStyleCategory = (): OmitStrict<StyleRelationshipType, 'id'> => ({
  ...groupStyleCategory,
  color: DEFAULT_GROUP_COLOR,
  size: DEFAULT_RELATIONSHIP_GROUP_SIZE,
});

export const getDefaultRelStyling = (
  type: PerspectiveRelationshipType['id'],
): OmitStrict<StyleRelationshipType, 'id'> => ({
  color: DEFAULT_RELATIONSHIP_COLOR,
  size: DEFAULT_RELATIONSHIP_SIZE,
  captionKeys: [], // make caption backward-compatible for <= Bloom 2.7.1
  captions: [
    // caption for >= Bloom 2.8.0
    {
      inTooltip: true,
      isCaption: true,
      styles: [],
      key: type,
      type: CAPTION_TYPE_REL,
    },
  ],
  textSize: DEFAULT_UNCATEGORISED_TEXT_SIZE,
  textAlign: DEFAULT_UNCATEGORISED_TEXT_ALIGN_RELATIONSHIP,
  styleRules: [],
});

const getStyleFromStoredCategory = (cat: CategoryWithStyle): StyleCategory => {
  return {
    id: cat.id,
    color: cat.color,
    size: cat.size,
    icon: cat.icon,
    textSize: cat.textSize,
    textAlign: cat.textAlign,
    captions: cat.captions,
    captionKeys: cat.captionKeys, // make caption backward-compatible for <= Bloom 2.7.1
    styleRules: cat.styleRules ?? [],
  };
};

const getStyleFromStoredRelationshipType = (relType: RelationshipTypeWithStyle): StyleRelationshipType => {
  return {
    id: relType.id,
    color: relType.color,
    size: relType.size,
    textSize: relType.textSize,
    textAlign: relType.textAlign,
    captions: relType.captions,
    captionKeys: relType.captionKeys, // make caption backward-compatible for <= Bloom 2.7.1
    styleRules: relType.styleRules ?? [],
  };
};

const getStyleEntryFromPerspective = (perspective: PerspectiveWithStyle): Style => {
  return {
    id: perspective.id,
    type: STYLE_TYPE_PERSPECTIVE,
    palette: perspective.palette ?? { colors: defaultPalette, currentIndex: 0 },
    categories: perspective.categories?.map(getStyleFromStoredCategory),
    relationshipTypes: perspective.relationshipTypes?.map(getStyleFromStoredRelationshipType),
  };
};

const getStyleEntryFromScene = (scene: Scene): Style => {
  return {
    palette: { colors: defaultPalette, currentIndex: 0 },
    ...scene.style,
    id: scene.id,
    type: STYLE_TYPE_SCENE,
    relationshipTypes: scene.style?.relationshipTypes ?? [],
    categories: scene.style?.categories ?? [],
  };
};

export interface StyleState {
  styles: Record<Style['id'], Style>;
  currentStyleId: Nullable<Style['id']>;
  currentStyleType: typeof STYLE_TYPE_SCENE | typeof STYLE_TYPE_PERSPECTIVE;
}

export const initialState: StyleState = {
  styles: {},
  currentStyleId: null,
  currentStyleType: STYLE_TYPE_SCENE,
};

const setLatestPerspectiveVersion = (perspective: Perspective) => ({
  ...perspective,
  version: getLatestPerspectiveVersion(),
});

const stylesSlice = createSlice({
  name: NAME,
  initialState,
  reducers: {
    addCategories(
      state,
      action: PayloadAction<{
        categorieIds: Category['id'][];
        styleId?: Style['id'];
      }>,
    ) {
      const { categorieIds, styleId } = action.payload;
      const actionStyleId = styleId ?? state.currentStyleId;
      const style = !isNil(actionStyleId) ? state.styles[actionStyleId] : undefined;
      if (isNil(style) || isNil(actionStyleId)) return;
      const palette = style.palette ?? { colors: defaultPalette, currentIndex: 0 };
      style.categories = style.categories ?? [];
      for (const id of categorieIds) {
        const color = palette.colors[palette.currentIndex % palette.colors.length];
        color !== undefined &&
          style.categories.push({
            ...getOtherCategory(),
            id,
            color,
          });
        palette.currentIndex += 1;
      }
      state.styles[actionStyleId] = style;
    },
    removeCategories(
      state,
      action: PayloadAction<{
        categorieIds: Category['id'][];
        styleId?: Style['id'];
      }>,
    ) {
      const { categorieIds, styleId } = action.payload;
      const actionStyleId = styleId ?? state.currentStyleId;
      const style = !isNil(actionStyleId) ? state.styles[actionStyleId] : undefined;
      if (isNil(style) || isNil(actionStyleId)) return;
      style.categories = style.categories?.filter(({ id }) => !categorieIds.includes(id)) ?? [];
      state.styles[actionStyleId] = style;
    },
    addRelationshipTypes(
      state,
      action: PayloadAction<{ relationshipTypes: PerspectiveRelationshipType['id'][]; styleId?: Style['id'] }>,
    ) {
      const { relationshipTypes, styleId } = action.payload;
      const actionStyleId = styleId ?? state.currentStyleId;
      const style = !isNil(actionStyleId) ? state.styles[actionStyleId] : undefined;
      if (isNil(style) || isNil(actionStyleId)) return;
      if (isNil(style.relationshipTypes)) style.relationshipTypes = [];
      style.relationshipTypes = [
        ...style.relationshipTypes,
        ...relationshipTypes.map((id) => ({ ...getDefaultRelStyling(id), id })),
      ];
      state.styles[actionStyleId] = style;
    },
    removeRelationshipTypes(
      state,
      action: PayloadAction<{ relationshipTypes: PerspectiveRelationshipType['id'][]; styleId?: Style['id'] }>,
    ) {
      const { relationshipTypes, styleId } = action.payload;
      const actionStyleId = styleId ?? state.currentStyleId;
      const style = !isNil(actionStyleId) ? state.styles[actionStyleId] : undefined;
      if (isNil(style) || isNil(actionStyleId) || isNil(style.relationshipTypes)) return;
      style.relationshipTypes = style.relationshipTypes.filter(({ id }) => !relationshipTypes.includes(id)) ?? [];
      state.styles[actionStyleId] = style;
    },
    setStyleById(state, action: PayloadAction<{ styleId: Style['id']; style: Style }>) {
      const { styleId, style } = action.payload;
      state.styles[styleId] = style;
    },
    setCurrentStyleId(state, action: PayloadAction<Style['id']>) {
      const styleId = action.payload;
      state.currentStyleType = state.styles[styleId]?.type ?? STYLE_TYPE_PERSPECTIVE;
      state.currentStyleId = styleId;
    },
    addStyleRuleForCategory(
      state,
      action: PayloadAction<{
        categoryId: Category['id'];
        styleRule: StyleRule;
      }>,
    ) {
      const { categoryId, styleRule } = action.payload;
      const { currentStyleId } = state;
      const style = !isNil(currentStyleId) ? state.styles[currentStyleId] : undefined;
      const category =
        !isNil(style) && !isNil(categoryId) ? style.categories?.find(({ id }) => id === categoryId) : undefined;
      if (!isNil(category)) {
        category.styleRules = [...(category.styleRules ?? []), styleRule];
      }
    },
    addStyleRuleForRelationship(
      state,
      action: PayloadAction<{
        relType: PerspectiveRelationshipType['id'];
        styleRule: StyleRule;
      }>,
    ) {
      const { relType, styleRule } = action.payload;
      const { currentStyleId } = state;
      const style = !isNil(currentStyleId) ? state.styles[currentStyleId] : undefined;
      if (isNil(style)) return;
      if (isNil(style.relationshipTypes)) style.relationshipTypes = [];
      const currentRelType = style.relationshipTypes.find(({ id }) => id === relType);
      if (!isNil(currentRelType)) {
        currentRelType.styleRules = [...(currentRelType.styleRules ?? []), styleRule];
      }
    },
    deleteStyleRuleForCategory(
      state,
      action: PayloadAction<{
        categoryId: Category['id'];
        styleRuleIndex: number;
      }>,
    ) {
      const { categoryId, styleRuleIndex } = action.payload;
      const { currentStyleId } = state;
      const style = !isNil(currentStyleId) ? state.styles[currentStyleId] : undefined;
      const category = style?.categories?.find(({ id }) => id === categoryId);
      if (isNil(style) || isNil(category) || isNil(style.categories)) return;
      category.styleRules?.splice(styleRuleIndex, 1);
    },
    deleteStyleRulesByRuleId(state, action: PayloadAction<{ styleRuleId: StyleRule['id']; styleId?: Style['id'] }>) {
      const { styleRuleId, styleId } = action.payload;
      const actionStyleId = styleId ?? state.currentStyleId;
      const style = !isNil(actionStyleId) ? state.styles[actionStyleId] : undefined;
      if (isNil(style) || isNil(actionStyleId)) return;
      style.categories?.forEach((category) => {
        const index = category.styleRules?.findIndex(({ id }) => id === styleRuleId);
        if (index !== undefined && index >= 0) category.styleRules?.splice(index, 1);
      });
      state.styles[actionStyleId] = style;
    },
    deleteStyleRuleForRelationship(
      state,
      action: PayloadAction<{
        styleRuleIndex: number;
        relType: string;
      }>,
    ) {
      const { styleRuleIndex, relType } = action.payload;
      const { currentStyleId } = state;
      const style = !isNil(currentStyleId) ? state.styles[currentStyleId] : undefined;
      if (isNil(style) || isNil(style.relationshipTypes)) return;
      const relTypeDetails = style.relationshipTypes.find(({ id }) => id === relType);
      if (isNil(relTypeDetails)) return;
      relTypeDetails.styleRules?.splice(styleRuleIndex, 1);
    },
    updateColorForCategory(
      state,
      action: PayloadAction<{
        categoryId: Category['id'];
        newColor: string;
        styleId?: Style['id'];
      }>,
    ) {
      const { categoryId, newColor, styleId } = action.payload;
      const actionStyleId = styleId ?? state.currentStyleId;
      const style = !isNil(actionStyleId) ? state.styles[actionStyleId] : undefined;
      if (isNil(style) || isNil(actionStyleId)) return;
      style.categories = style.categories?.map((category) => {
        if (category.id === categoryId) {
          return {
            ...category,
            color: newColor,
          };
        }
        return category;
      });
      state.styles[actionStyleId] = style;
    },
    updateStyleRuleForCategory(
      state,
      action: PayloadAction<{
        categoryId: Category['id'];
        styleRuleUpdate: Partial<StyleRule>;
        styleRuleIndex: number;
        styleId?: Style['id'];
      }>,
    ) {
      const { categoryId, styleRuleUpdate, styleRuleIndex, styleId } = action.payload;
      const actionStyleId = styleId ?? state.currentStyleId;
      const style = !isNil(actionStyleId) ? state.styles[actionStyleId] : undefined;
      if (isNil(style) || isNil(actionStyleId)) return;
      style.categories = style.categories?.map((category) => {
        if (category.id === categoryId) {
          return {
            ...category,
            styleRules: category.styleRules.map((rule, index) => {
              if (index === styleRuleIndex) {
                return {
                  ...rule,
                  ...styleRuleUpdate,
                };
              }
              return rule;
            }),
          };
        }
        return category;
      });
      state.styles[actionStyleId] = style;
    },
    updateStyleRuleForRelationship(
      state,
      action: PayloadAction<{
        relType: BloomRelationship['id'];
        styleRuleUpdate: Partial<StyleRule>;
        styleRuleIndex: number;
        styleId?: Style['id'];
      }>,
    ) {
      const { relType, styleRuleUpdate, styleRuleIndex, styleId } = action.payload;
      const actionStyleId = styleId ?? state.currentStyleId;
      const style = !isNil(actionStyleId) ? state.styles[actionStyleId] : undefined;
      if (isNil(style) || isNil(actionStyleId)) return;
      if (isNil(style.relationshipTypes)) style.relationshipTypes = [];
      style.relationshipTypes = style.relationshipTypes.map((rel) => {
        if (rel.id === relType) {
          return {
            ...rel,
            styleRules: rel.styleRules?.map((rule, index) => {
              if (index === styleRuleIndex) {
                return {
                  ...rule,
                  ...styleRuleUpdate,
                };
              }
              return rule;
            }),
          };
        }
        return rel;
      });
      state.styles[actionStyleId] = style;
    },
    updateSizeForCategory(
      state,
      action: PayloadAction<{
        categoryId: Category['id'];
        newSize: number;
        styleId?: Style['id'];
      }>,
    ) {
      const { categoryId, newSize, styleId } = action.payload;
      const actionStyleId = styleId ?? state.currentStyleId;
      const style = !isNil(actionStyleId) ? state.styles[actionStyleId] : undefined;
      if (isNil(style) || isNil(actionStyleId)) return;
      style.categories = style.categories?.map((category) => {
        if (category.id === categoryId) {
          return {
            ...category,
            size: newSize,
          };
        }
        return category;
      });
      const actionStyle = state.styles[actionStyleId];
      if (actionStyle === undefined) return;
      actionStyle.categories = style.categories;
    },
    updateTextSizeForCategory(
      state,
      action: PayloadAction<{
        categoryId: Category['id'];
        newSize: number;
        styleId?: Style['id'];
      }>,
    ) {
      const { categoryId, newSize, styleId } = action.payload;
      const actionStyleId = styleId ?? state.currentStyleId;
      const style = !isNil(actionStyleId) ? state.styles[actionStyleId] : undefined;
      if (isNil(style) || isNil(actionStyleId)) return;
      style.categories = style.categories?.map((category) => {
        if (category.id === categoryId) {
          return {
            ...category,
            textSize: newSize,
          };
        }
        return category;
      });
      state.styles[actionStyleId] = style;
    },
    updateTextAlignmentForCategory(
      state,
      action: PayloadAction<{
        categoryId: Category['id'];
        newAlignment: string;
        styleId?: Style['id'];
      }>,
    ) {
      const { categoryId, newAlignment, styleId } = action.payload;
      const actionStyleId = styleId ?? state.currentStyleId;
      const style = !isNil(actionStyleId) ? state.styles[actionStyleId] : undefined;
      if (isNil(style) || isNil(actionStyleId)) return;
      style.categories = style.categories?.map((category) => {
        if (category.id === categoryId) {
          return {
            ...category,
            textAlign: newAlignment,
          };
        }
        return category;
      });
      state.styles[actionStyleId] = style;
    },
    updateColorForRelType(
      state,
      action: PayloadAction<{
        relType: string;
        newColor: string;
        styleId?: Style['id'];
      }>,
    ) {
      const { relType, newColor, styleId } = action.payload;
      const actionStyleId = styleId ?? state.currentStyleId;
      const style = !isNil(actionStyleId) ? state.styles[actionStyleId] : undefined;
      if (isNil(style) || isNil(actionStyleId)) return;
      style.relationshipTypes = style.relationshipTypes?.map((rel) => {
        if (rel.id === relType) {
          return {
            ...rel,
            color: newColor,
          };
        }
        return rel;
      });
      state.styles[actionStyleId] = style;
    },
    updateSizeForRelType(
      state,
      action: PayloadAction<{
        relType: string;
        newSize: number;
        styleId?: Style['id'];
      }>,
    ) {
      const { relType, newSize, styleId } = action.payload;
      const actionStyleId = styleId ?? state.currentStyleId;
      const style = !isNil(actionStyleId) ? state.styles[actionStyleId] : undefined;
      if (isNil(style) || isNil(actionStyleId)) return;
      style.relationshipTypes = style.relationshipTypes?.map((rel) => {
        if (rel.id === relType) {
          return {
            ...rel,
            size: newSize,
          };
        }
        return rel;
      });
      state.styles[actionStyleId] = style;
    },
    updateTextSizeForRelType(
      state,
      action: PayloadAction<{
        relType: string;
        newSize: number;
        styleId?: Style['id'];
      }>,
    ) {
      const { relType, newSize, styleId } = action.payload;
      const actionStyleId = styleId ?? state.currentStyleId;
      const style = !isNil(actionStyleId) ? state.styles[actionStyleId] : undefined;
      if (isNil(style) || isNil(actionStyleId)) return;
      style.relationshipTypes = style.relationshipTypes?.map((rel) => {
        if (rel.id === relType) {
          return {
            ...rel,
            textSize: newSize,
          };
        }
        return rel;
      });
      state.styles[actionStyleId] = style;
    },
    updateTextAlignmentForRelType(
      state,
      action: PayloadAction<{
        relType: string;
        newAlignment: string;
        styleId?: Style['id'];
      }>,
    ) {
      const { relType, newAlignment, styleId } = action.payload;
      const actionStyleId = styleId ?? state.currentStyleId;
      const style = !isNil(actionStyleId) ? state.styles[actionStyleId] : undefined;
      if (isNil(style) || isNil(actionStyleId)) return;
      style.relationshipTypes = style.relationshipTypes?.map((rel) => {
        if (rel.id === relType) {
          return {
            ...rel,
            textAlign: newAlignment,
          };
        }
        return rel;
      });
      state.styles[actionStyleId] = style;
    },
    updateIconForCategory(
      state,
      action: PayloadAction<{ categoryId: Category['id']; newIcon: CategoryIcon; styleId?: Style['id'] }>,
    ) {
      const { categoryId, newIcon, styleId } = action.payload;
      const actionStyleId = styleId ?? state.currentStyleId;
      const style = !isNil(actionStyleId) ? state.styles[actionStyleId] : undefined;
      if (isNil(style) || isNil(actionStyleId)) return;
      style.categories = style.categories?.map((category) => {
        if (category.id === categoryId) {
          return {
            ...category,
            icon: newIcon,
          };
        }
        return category;
      });
      state.styles[actionStyleId] = style;
    },
    updateCaptionStylesForCategory(
      state,
      action: PayloadAction<{ categoryId: Category['id']; caption: Caption; styles: CaptionStyle[] }>,
    ) {
      const { categoryId, caption, styles } = action.payload;
      const actionStyleId = state.currentStyleId;
      const style = !isNil(actionStyleId) ? state.styles[actionStyleId] : undefined;
      if (isNil(style) || isNil(actionStyleId)) return;
      style.categories = style.categories?.map((category) => {
        if (category.id === categoryId) {
          const captionToUpdate = category.captions?.find((c) => c.key === caption.key && c.type === caption.type);
          if (captionToUpdate !== undefined) {
            captionToUpdate.styles = styles;
          }
          return { ...category };
        }
        return category;
      });
      state.styles[actionStyleId] = style;
    },
    updateCaptionTooltipForCategory(
      state,
      action: PayloadAction<{ categoryId: Category['id']; caption: Caption; styleId?: Style['id'] }>,
    ) {
      const { categoryId, caption, styleId } = action.payload;
      const actionStyleId = styleId ?? state.currentStyleId;
      const style = !isNil(actionStyleId) ? state.styles[actionStyleId] : undefined;
      if (isNil(style) || isNil(actionStyleId)) return;
      style.categories = style.categories?.map((category) => {
        if (category.id === categoryId) {
          return {
            ...category,
            captions: category.captions.map((c) => {
              if (c.key === caption?.key && c.type === caption?.type) {
                return {
                  ...c,
                  inTooltip: !caption.inTooltip,
                };
              }
              return c;
            }),
          };
        }
        return category;
      });
      state.styles[actionStyleId] = style;
    },
    addCaptionKeyForCategory(
      state,
      action: PayloadAction<{ categoryId: Category['id']; caption: Caption; styleId?: Style['id'] }>,
    ) {
      const { categoryId, caption, styleId } = action.payload;
      const actionStyleId = styleId ?? state.currentStyleId;
      const style = !isNil(actionStyleId) ? state.styles[actionStyleId] : undefined;
      if (isNil(style) || isNil(actionStyleId)) return;
      style.categories = style.categories?.map((category) => {
        if (category.id === categoryId) {
          return {
            ...category,
            captions: category.captions.reduce((acc: Caption[], c) => {
              if (c.key === caption?.key && c.type === caption?.type) {
                acc = [...acc, { ...c, isCaption: true }];
              } else {
                acc = [...acc, c];
              }
              return acc;
            }, []),
          };
        }
        return category;
      });
      state.styles[actionStyleId] = style;
    },
    removeCaptionKeyForCategory(
      state,
      action: PayloadAction<{ categoryId: Category['id']; caption: Caption; styleId?: Style['id'] }>,
    ) {
      const { categoryId, caption, styleId } = action.payload;
      const actionStyleId = styleId ?? state.currentStyleId;
      const style = !isNil(actionStyleId) ? state.styles[actionStyleId] : undefined;
      if (isNil(style) || isNil(actionStyleId)) return;
      style.categories = style.categories?.map((category) => {
        if (category.id === categoryId) {
          return {
            ...category,
            captions: category.captions.reduce((acc: Caption[], c) => {
              if (c.key === caption?.key && c.type === caption?.type) {
                acc = [...acc, { ...c, isCaption: false }];
              } else {
                acc = [...acc, c];
              }
              return acc;
            }, []),
          };
        }
        return category;
      });
      state.styles[actionStyleId] = style;
    },
    forceRemoveCaptionKeyForCategory(
      state,
      action: PayloadAction<{ categoryId: Category['id']; caption: Caption; styleId?: Style['id'] }>,
    ) {
      const { categoryId, caption, styleId } = action.payload;
      const actionStyleId = styleId ?? state.currentStyleId;
      const style = !isNil(actionStyleId) ? state.styles[actionStyleId] : undefined;
      if (isNil(style) || isNil(actionStyleId)) return;
      style.categories = style.categories?.map((category) => {
        if (category.id === categoryId) {
          return {
            ...category,
            captions: category.captions.filter((c) => c.key !== caption.key),
          };
        }
        return category;
      });
      state.styles[actionStyleId] = style;
    },
    forceRemoveCaptionKeyForRelType(
      state,
      action: PayloadAction<{ relType: string; caption: Caption; styleId?: Style['id'] }>,
    ) {
      const { relType, caption, styleId } = action.payload;
      const actionStyleId = styleId ?? state.currentStyleId;
      const style = !isNil(actionStyleId) ? state.styles[actionStyleId] : undefined;
      if (isNil(style) || isNil(actionStyleId)) return;
      style.relationshipTypes = style.relationshipTypes?.map((rel) => {
        if (rel.id === relType) {
          return {
            ...rel,
            captions: rel.captions.filter((c) => c.key !== caption.key),
          };
        }
        return rel;
      });
      state.styles[actionStyleId] = style;
    },
    addCaptionKeyForRelType(
      state,
      action: PayloadAction<{ relType: string; caption: Caption; styleId?: Style['id'] }>,
    ) {
      const { relType, caption, styleId } = action.payload;
      const actionStyleId = styleId ?? state.currentStyleId;
      const style = !isNil(actionStyleId) ? state.styles[actionStyleId] : undefined;
      if (isNil(style) || isNil(actionStyleId)) return;
      style.relationshipTypes = style.relationshipTypes?.map((rel) => {
        if (rel.id === relType) {
          return {
            ...rel,
            captions: rel.captions.reduce((acc: Caption[], c) => {
              if (c.key === caption?.key && c.type === caption?.type) {
                acc = [...acc, { ...c, isCaption: true }];
              } else {
                acc = [...acc, c];
              }
              return acc;
            }, []),
          };
        }
        return rel;
      });
      state.styles[actionStyleId] = style;
    },
    removeCaptionKeyForRelType(
      state,
      action: PayloadAction<{ relType: string; caption: Caption; styleId?: Style['id'] }>,
    ) {
      const { relType, caption, styleId } = action.payload;
      const actionStyleId = styleId ?? state.currentStyleId;
      const style = !isNil(actionStyleId) ? state.styles[actionStyleId] : undefined;
      if (isNil(style) || isNil(actionStyleId)) return;
      style.relationshipTypes = style.relationshipTypes?.map((rel) => {
        if (rel.id === relType) {
          return {
            ...rel,
            captions: rel.captions.reduce((acc: Caption[], c) => {
              if (c.key === caption?.key && c.type === caption?.type) {
                acc = [...acc, { ...c, isCaption: false }];
              } else {
                acc = [...acc, c];
              }
              return acc;
            }, []),
          };
        }
        return rel;
      });
      state.styles[actionStyleId] = style;
    },
    updateCaptionStylesForRelType(
      state,
      action: PayloadAction<{ relType: string; captionKey: string; styles: CaptionStyle[]; styleId?: Style['id'] }>,
    ) {
      const { relType, captionKey, styles, styleId } = action.payload;
      const actionStyleId = styleId ?? state.currentStyleId;
      const style = !isNil(actionStyleId) ? state.styles[actionStyleId] : undefined;
      if (isNil(style) || isNil(actionStyleId)) return;
      style.relationshipTypes = style.relationshipTypes?.map((rel) => {
        if (rel.id === relType) {
          return {
            ...rel,
            captions: rel.captions.map((c) => {
              if (c.key === captionKey) {
                return {
                  ...c,
                  styles,
                };
              }
              return c;
            }),
          };
        }
        return rel;
      });
      state.styles[actionStyleId] = style;
    },
    updateCaptionTooltipForRelType(
      state,
      action: PayloadAction<{ relType: string; caption: Caption; styleId?: Style['id'] }>,
    ) {
      const { relType, caption, styleId } = action.payload;
      const actionStyleId = styleId ?? state.currentStyleId;
      const style = !isNil(actionStyleId) ? state.styles[actionStyleId] : undefined;
      if (isNil(style) || isNil(actionStyleId)) return;
      style.relationshipTypes = style.relationshipTypes?.map((rel) => {
        if (rel.id === relType) {
          return {
            ...rel,
            captions: rel.captions.map((c) => {
              if (c.key === caption?.key && c.type === caption?.type) {
                return {
                  ...c,
                  inTooltip: !caption.inTooltip,
                };
              }
              return c;
            }),
          };
        }
        return rel;
      });
      state.styles[actionStyleId] = style;
    },
    updateOrderOfCaptionsForCategory(
      state,
      action: PayloadAction<{
        categoryId: Category['id'];
        fromSceneActionIndex: number;
        toSceneActionIndex: number;
      }>,
    ) {
      const { categoryId, fromSceneActionIndex, toSceneActionIndex } = action.payload;
      const actionStyleId = state.currentStyleId;
      const style = !isNil(actionStyleId) ? state.styles[actionStyleId] : undefined;
      if (isNil(style) || isNil(actionStyleId)) return;
      style.categories = style.categories?.map((category) => {
        if (category.id === categoryId) {
          const [removed] = category.captions.splice(fromSceneActionIndex, 1);
          removed && category.captions.splice(toSceneActionIndex, 0, removed);
        }
        return category;
      });
      state.styles[actionStyleId] = style;
    },
    updateOrderOfCaptionsForRelationship(
      state,
      action: PayloadAction<{
        relType: string;
        fromSceneActionIndex: number;
        toSceneActionIndex: number;
      }>,
    ) {
      const { relType, fromSceneActionIndex, toSceneActionIndex } = action.payload;
      const actionStyleId = state.currentStyleId;
      const style = !isNil(actionStyleId) ? state.styles[actionStyleId] : undefined;
      if (isNil(style) || isNil(actionStyleId)) return;
      style.relationshipTypes = style.relationshipTypes?.map((rel) => {
        if (rel.id === relType) {
          const [removed] = rel.captions.splice(fromSceneActionIndex, 1);
          removed && rel.captions.splice(toSceneActionIndex, 0, removed);
        }
        return rel;
      });
      state.styles[actionStyleId] = style;
    },
    updateOrderOfCaptionsForCategoryRule(
      state,
      action: PayloadAction<{
        categoryId: Category['id'];
        fromSceneActionIndex: number;
        toSceneActionIndex: number;
        styleRuleIndex: number;
      }>,
    ) {
      const { categoryId, fromSceneActionIndex, toSceneActionIndex, styleRuleIndex } = action.payload;
      const actionStyleId = state.currentStyleId;
      const style = !isNil(actionStyleId) ? state.styles[actionStyleId] : undefined;
      if (isNil(style) || isNil(actionStyleId)) return;
      const actionStyle = state.styles[actionStyleId];
      if (actionStyle === undefined) return;
      actionStyle.categories = actionStyle.categories?.map((category) => {
        if (category.id === categoryId && !isNil(category.styleRules)) {
          const categoryStyleRule = category.styleRules[styleRuleIndex];
          if (categoryStyleRule === undefined) return category;
          categoryStyleRule.captions = getOrderedList(
            [...(categoryStyleRule.captions ?? [])],
            fromSceneActionIndex,
            toSceneActionIndex,
          );
        }
        return category;
      });
    },
    updateOrderOfCaptionsForRelationshipRule(
      state,
      action: PayloadAction<{
        relType: string;
        fromSceneActionIndex: number;
        toSceneActionIndex: number;
        styleRuleIndex: number;
      }>,
    ) {
      const { relType, fromSceneActionIndex, toSceneActionIndex, styleRuleIndex } = action.payload;
      const actionStyleId = state.currentStyleId;
      const style = !isNil(actionStyleId) ? state.styles[actionStyleId] : undefined;
      if (isNil(style) || isNil(actionStyleId)) return;
      const actionStyle = state.styles[actionStyleId];
      if (actionStyle === undefined) return;
      actionStyle.relationshipTypes = actionStyle.relationshipTypes?.map((rel) => {
        if (rel.id === relType && !isNil(rel.styleRules)) {
          const relStyleRule = rel.styleRules[styleRuleIndex];
          if (relStyleRule === undefined) return rel;
          relStyleRule.captions = getOrderedList(
            [...(relStyleRule.captions ?? [])],
            fromSceneActionIndex,
            toSceneActionIndex,
          );
        }
        return rel;
      });
    },
    setCaptionsForCategory(
      state,
      action: PayloadAction<{ categoryId: Category['id']; newCaptions: Caption[]; styleId?: Style['id'] }>,
    ) {
      const { categoryId, newCaptions, styleId } = action.payload;
      const actionStyleId = styleId ?? state.currentStyleId;
      const style = !isNil(actionStyleId) ? state.styles[actionStyleId] : undefined;
      if (isNil(style) || isNil(actionStyleId)) return;
      const actionStyle = state.styles[actionStyleId];
      if (actionStyle === undefined) return;
      actionStyle.categories = actionStyle.categories?.map((category) => {
        if (category.id === categoryId) {
          return {
            ...category,
            captions: newCaptions,
          };
        }
        return category;
      });
    },
    setCaptionsForRelationship(state, action: PayloadAction<{ relType: string; newCaptions: Caption[] }>) {
      const { relType, newCaptions } = action.payload;
      const actionStyleId = state.currentStyleId;
      const style = !isNil(actionStyleId) ? state.styles[actionStyleId] : undefined;
      if (isNil(style) || isNil(actionStyleId)) return;
      const actionStyle = state.styles[actionStyleId];
      if (actionStyle === undefined) return;
      actionStyle.relationshipTypes = actionStyle.relationshipTypes?.map((rel) => {
        if (rel.id === relType) {
          return {
            ...rel,
            captions: newCaptions,
          };
        }
        return rel;
      });
    },
  },
  extraReducers: (builder) => {
    builder.addCase(REHYDRATE, (state, action: AnyAction) => {
      if (isNil(action.payload) || Object.keys(action.payload).length === 0) return;
      if (Array.isArray(action.payload.perspectives?.perspectives)) {
        const migrated = migrate(action.payload.perspectives, trasformations, action.payload.version);

        const perspectiveStyles = (
          migrated.perspectives.map(setLatestPerspectiveVersion) as Nullable<PerspectiveWithStyle>[]
        )
          .filter((persp): persp is PerspectiveWithStyle => !isNil(persp))
          .map(getStyleEntryFromPerspective);
        const newPerspectiveStyles = keyBy(perspectiveStyles, 'id');
        state.styles = { ...(state.styles ?? {}), ...newPerspectiveStyles };

        if (!isNil(action.payload.currentPerspectiveId)) {
          state.currentStyleType = STYLE_TYPE_PERSPECTIVE;
          state.currentStyleId = action.payload.currentPerspectiveId;
        }
        if (!isNil(action.payload.scene?.currentSceneId)) {
          state.currentStyleType = STYLE_TYPE_SCENE;
          state.currentStyleId = action.payload.scene?.currentSceneId;
        }
      }
      const styles = action.payload?.styles?.styles as Record<string, Style> | undefined;
      if (styles) {
        state.styles = { ...state.styles, ...styles };
      }
    });
    builder.addCase(addPerspective, (state, action) => {
      const perspective = action.payload;
      if (isNil(perspective)) return;
      // @ts-expect-error TODO typing for this is broken, because we run this on perspectives without styles as well
      const style = getStyleEntryFromPerspective(perspective);
      state.styles[style.id] = style;
    });
    builder.addCase(loadPerspective, (state, action) => {
      const perspective = action.payload;
      if (isNil(perspective)) return;
      const style = getStyleEntryFromPerspective(perspective);
      state.styles[style.id] = style;
    });
    builder.addCase(removePerspective, (state, action) => {
      const { perspectiveId } = action.payload;
      delete state.styles[perspectiveId];
    });
    builder.addCase(clearPerspectives, (state) => {
      Object.values(state.styles)
        .filter(({ type }) => type === STYLE_TYPE_PERSPECTIVE)
        .map(({ id }) => id)
        .forEach((id) => delete state.styles[id]);
    });
    builder.addCase(addScene, (state, action) => {
      const newScene = action.payload;
      if (!isNil(newScene.style)) {
        const styleSceneEntry = getStyleEntryFromScene(newScene);
        state.styles[styleSceneEntry.id] = styleSceneEntry;
      }
    });
    builder.addCase(duplicateScene, (state, action) => {
      const scene = action.payload;
      if (!isNil(scene.style)) {
        const styleSceneEntry = getStyleEntryFromScene(scene);
        state.styles[styleSceneEntry.id] = styleSceneEntry;
      }
    });
    builder.addCase(fetchSceneThunk.fulfilled, (state, action) => {
      const loadedScene = action.payload;
      if (!isNil(loadedScene) && !isNil(loadedScene.style) && !isNil(loadedScene.id)) {
        state.styles[loadedScene.id] = loadedScene.style;
      }
    });
    builder.addCase(deleteSceneThunk.fulfilled, (state, action) => {
      const sceneId = action.payload;
      delete state.styles[sceneId];
    });
    builder.addCase(storeSceneThunk.fulfilled, (state, action) => {
      const { scene: newScene, requestId: oldId } = action.payload;
      delete state.styles[oldId];

      if (!isNil(newScene.style)) {
        const { id, style } = newScene;
        state.styles[id] = style;

        if (state.currentStyleId === oldId) {
          state.currentStyleId = id;
        }
      }
    });
    builder.addCase(clearScenes, (state) => {
      Object.values(state.styles)
        .filter(({ type }) => type === STYLE_TYPE_SCENE)
        .map(({ id }) => id)
        .forEach((id) => delete state.styles[id]);
    });
  },
});

export const {
  addCategories,
  removeCategories,
  addRelationshipTypes,
  removeRelationshipTypes,
  setStyleById,
  setCurrentStyleId,
  addStyleRuleForCategory,
  addStyleRuleForRelationship,
  deleteStyleRuleForCategory,
  deleteStyleRulesByRuleId,
  deleteStyleRuleForRelationship,
  updateColorForCategory,
  updateStyleRuleForCategory,
  updateStyleRuleForRelationship,
  updateSizeForCategory,
  updateTextSizeForCategory,
  updateTextAlignmentForCategory,
  updateColorForRelType,
  updateSizeForRelType,
  updateTextSizeForRelType,
  updateTextAlignmentForRelType,
  updateIconForCategory,
  updateCaptionStylesForCategory,
  updateCaptionTooltipForCategory,
  addCaptionKeyForCategory,
  removeCaptionKeyForCategory,
  forceRemoveCaptionKeyForCategory,
  forceRemoveCaptionKeyForRelType,
  addCaptionKeyForRelType,
  removeCaptionKeyForRelType,
  updateCaptionStylesForRelType,
  updateCaptionTooltipForRelType,
  updateOrderOfCaptionsForCategory,
  updateOrderOfCaptionsForRelationship,
  updateOrderOfCaptionsForCategoryRule,
  updateOrderOfCaptionsForRelationshipRule,
  setCaptionsForCategory,
  setCaptionsForRelationship,
} = stylesSlice.actions;

export default stylesSlice.reducer;

export type StylesAction = SliceActionsUnion<typeof stylesSlice.actions>;
